import { EventType } from '@tanstack/ai';
import { stream } from '@tanstack/ai-client';

import { openChatStream } from '../api/chat.api';
import {
  renderDefaultOutputSchema,
  renderFileChartOutputSchema,
  renderForwardChartOutputSchema,
  renderSeasonalChartOutputSchema,
  renderSpotChartOutputSchema,
  renderStaticChartOutputSchema,
  renderTableOutputSchema
} from '../tools/displayTools';
import { parseStreamLine } from './parsing';

import type { StreamChunk } from '@tanstack/ai';
import type { ConnectConnectionAdapter } from '@tanstack/ai-client';
import type { z } from 'zod';
import type { StreamEvent, SendMessageArgs } from '../types/chat.types';

const DISPLAY_TO_TOOL_NAME: Record<string, string> = {
  table: 'render_table',
  default: 'render_default',
  spot_chart: 'render_spot_chart',
  seasonal_chart: 'render_seasonal_chart',
  forward_chart: 'render_forward_chart',
  file_chart: 'render_file_chart',
  static_chart: 'render_static_chart'
};

const OUTPUT_SCHEMA_BY_TOOL_NAME: Record<string, z.ZodType> = {
  render_table: renderTableOutputSchema,
  render_default: renderDefaultOutputSchema,
  render_spot_chart: renderSpotChartOutputSchema,
  render_seasonal_chart: renderSeasonalChartOutputSchema,
  render_forward_chart: renderForwardChartOutputSchema,
  render_file_chart: renderFileChartOutputSchema,
  render_static_chart: renderStaticChartOutputSchema
};

// Only gate tool calls whose schema encodes a single documented backend
// contract on a required top-level field (STATIC_CHART_RESPONSE_CONTRACT.md's
// `chartConfig`, and file_chart's `seriesFileName`/`configFileName` — the
// same fields `parseStreamLine`'s predecessor already special-cased). The
// row-array schemas (table/default/spot/seasonal/forward) validate every
// `CatalogRow`/`ChartResultRow` field deeply; the backend's catalog search
// legitimately returns partial rows, so gating on those schemas would reject
// valid, historically-accepted payloads instead of catching a real contract
// violation.
const VALIDATED_TOOL_NAMES = new Set(['render_static_chart', 'render_file_chart']);

// The backend calls one generic tool for every chart type (observed on the
// wire as `tool_call.data.name: "display_chart"`, not per-chart names), so
// `handleToolCallEvent` can't learn which widget to render from the tool_call
// event — and once a tool-call part exists, TanStack AI's stream processor
// never renames it (`handleToolCallResultEvent` only ever updates `output`/
// `state`, see `updateToolCallWithOutput`). `display_type` on the *result* is
// the only place the real chart type is known, so `widgetTool` is computed
// here and carried inside the output itself; `AIContent` reads it back out
// instead of trusting the tool-call's `name` for widget resolution.
function toolOutputFromResult(
  evt: Extract<StreamEvent, { type: 'tool_result' }>
): { widgetTool: string } & Record<string, unknown> {
  const md = evt.data.result.metadata;
  const display = md?.display_type ?? 'default';
  const widgetTool = DISPLAY_TO_TOOL_NAME[display] ?? 'render_default';
  switch (display) {
    case 'table':
    case 'default':
      return {
        widgetTool,
        rows: evt.data.result.results ?? [],
        metadata: { start_date: md?.start_date, end_date: md?.end_date }
      };
    case 'spot_chart':
    case 'seasonal_chart':
    case 'forward_chart':
      return {
        widgetTool,
        title: typeof md?.title === 'string' ? md.title : undefined,
        from: typeof md?.from === 'string' ? md.from : undefined,
        rows: evt.data.result.results ?? []
      };
    case 'file_chart':
      return {
        widgetTool,
        title: typeof md?.title === 'string' ? md.title : undefined,
        seriesFileName: md?.seriesFileName,
        configFileName: md?.configFileName,
        queryFileId: md?.queryFileId,
        downloadFileId: md?.downloadFileId,
        timeRangeButtons: md?.timeRangeButtons
      };
    case 'static_chart':
      // No `?? {}` fallback: an absent `chart_config` must surface as
      // `undefined` so the required-field check in
      // `renderStaticChartOutputSchema` (STATIC_CHART_RESPONSE_CONTRACT.md)
      // can actually catch the violation instead of being papered over by a
      // default empty object that vacuously satisfies `z.record(...)`.
      return {
        widgetTool,
        title: typeof md?.title === 'string' ? md.title : undefined,
        chartConfig: evt.data.result.chart_config
      };
    default:
      return { widgetTool, rows: evt.data.result.results ?? [] };
  }
}

interface StreamState {
  readonly messageId: string;
  readonly threadId: string;
  readonly runId: string;
  textStarted: boolean;
  readonly toolCallNameById: Map<string, string>;
}

function* handleContentEvent(
  evt: Extract<StreamEvent, { type: 'content' }>,
  state: StreamState
): Generator<StreamChunk> {
  const { messageId } = state;
  if (!state.textStarted) {
    state.textStarted = true;
    yield { type: EventType.TEXT_MESSAGE_START, messageId, role: 'assistant' };
  }
  // The backend's `content` events can replay the current text segment after a
  // tool call. Preserve that snapshot semantic when adapting to TanStack AI:
  // its stream processor reconciles `content` with the text already rendered,
  // whereas `delta` is always appended and would duplicate a replayed segment.
  yield {
    type: EventType.TEXT_MESSAGE_CONTENT,
    messageId,
    delta: '',
    content: evt.data.content
  };
}

function* handleToolCallEvent(
  evt: Extract<StreamEvent, { type: 'tool_call' }>,
  state: StreamState
): Generator<StreamChunk> {
  const { messageId } = state;
  const toolCallId = evt.data.id ?? `${messageId}-${evt.data.name}`;
  const toolCallName = DISPLAY_TO_TOOL_NAME[evt.data.name] ?? evt.data.name;
  state.toolCallNameById.set(toolCallId, toolCallName);
  yield {
    type: EventType.TOOL_CALL_START,
    toolCallId,
    toolCallName,
    toolName: toolCallName,
    parentMessageId: messageId
  };
  yield { type: EventType.TOOL_CALL_ARGS, toolCallId, delta: JSON.stringify(evt.data.args) };
  yield { type: EventType.TOOL_CALL_END, toolCallId };
}

function buildToolResultChunk(
  evt: Extract<StreamEvent, { type: 'tool_result' }>,
  state: StreamState
): StreamChunk {
  const toolCallId = evt.data.tool_call_id;
  const output = toolOutputFromResult(evt);
  // Validate against the tool name resolved from `display_type` (see
  // `toolOutputFromResult`), not `state.toolCallNameById` — the tool_call
  // event's name doesn't vary per chart type on the real backend, so it was
  // never a reliable key for schema selection either.
  const { widgetTool } = output;
  const schema = VALIDATED_TOOL_NAMES.has(widgetTool)
    ? OUTPUT_SCHEMA_BY_TOOL_NAME[widgetTool]
    : undefined;
  const validation = schema?.safeParse(output);
  // Backend contract violation (e.g. a static_chart result with no
  // chart_config) — surface it on this specific tool call instead of
  // handing WidgetMapper a payload it wasn't built to render.
  const content =
    schema && !validation?.success
      ? JSON.stringify({ error: validation?.error.message ?? 'Invalid tool result' })
      : JSON.stringify(output);
  return {
    type: EventType.TOOL_CALL_RESULT,
    messageId: state.messageId,
    toolCallId,
    content,
    role: 'tool'
  };
}

function* handleCompleteEvent(state: StreamState): Generator<StreamChunk> {
  const { messageId, threadId, runId } = state;
  if (state.textStarted) yield { type: EventType.TEXT_MESSAGE_END, messageId };
  yield { type: EventType.RUN_FINISHED, threadId, runId };
}

function* chunksForEvent(evt: StreamEvent, state: StreamState): Generator<StreamChunk> {
  switch (evt.type) {
    case 'content':
      yield* handleContentEvent(evt, state);
      return;
    case 'tool_call':
      yield* handleToolCallEvent(evt, state);
      return;
    case 'tool_result':
      yield buildToolResultChunk(evt, state);
      return;
    case 'complete':
      yield* handleCompleteEvent(state);
      return;
  }
}

async function* toChunks(
  reader: ReadableStreamDefaultReader<Uint8Array>,
  runId: string,
  threadId: string
): AsyncGenerator<StreamChunk> {
  const decoder = new TextDecoder();
  const state: StreamState = {
    messageId: `${runId}-assistant`,
    threadId,
    runId,
    textStarted: false,
    toolCallNameById: new Map()
  };
  let buffer = '';

  yield { type: EventType.RUN_STARTED, threadId, runId };

  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() || '';

    for (const line of lines) {
      const evt = parseStreamLine(line);
      if (!evt) continue;
      yield* chunksForEvent(evt, state);
    }
  }
}

export function createTanstackConnection(params: SendMessageArgs): ConnectConnectionAdapter {
  // `stream()` does `yield* streamFactory(...)`, which requires the factory
  // to return an AsyncIterable synchronously — an `async (...) => {...}`
  // function instead returns a Promise<AsyncIterable>, which is not itself
  // async-iterable and throws "yield* (intermediate value) is not async
  // iterable" at runtime. An async generator function returns its generator
  // object synchronously, so it satisfies `yield*` directly.
  return stream(async function* (_messages, _data, abortSignal) {
    const runId = crypto.randomUUID();
    const threadId = params.conversationId;
    const reader = await openChatStream(params, abortSignal ?? new AbortController().signal);
    if (!reader) {
      yield { type: EventType.RUN_STARTED, threadId, runId };
      yield { type: EventType.RUN_ERROR, message: 'No response stream from server' };
      return;
    }
    yield* toChunks(reader, runId, threadId);
  });
}
